import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import { useAnimationSchedule } from './animation-schedule-context';

export function useScheduledVisibility(unitId, isAnimationDisabled, options = {}) {
  const { revealDelay = 0, blocksFollowing = false } = options;
  const { requestAnimation } = useAnimationSchedule();
  const [animation] = useState(() =>
    isAnimationDisabled ? { delay: 0, endAt: 0 } : requestAnimation(unitId, { duration: 0, blocksFollowing })
  );
  const visibilityDelay = animation.delay + revealDelay;
  const shouldShowImmediately = isAnimationDisabled || visibilityDelay === 0;
  const [isVisible, setVisible] = useState(shouldShowImmediately);

  useEffect(() => {
    if (shouldShowImmediately) {
      return undefined;
    }

    const visibilityTimer = setTimeout(() => setVisible(true), visibilityDelay);

    return () => clearTimeout(visibilityTimer);
  }, [shouldShowImmediately, visibilityDelay]);

  return { animation, isVisible: shouldShowImmediately || isVisible };
}

export function BaseScheduledNode({
  unitId,
  isAnimationDisabled = false,
  children,
  revealDelay = 0,
  blocksFollowing = false,
  onReveal = noop,
}) {
  const { isVisible } = useScheduledVisibility(unitId, isAnimationDisabled, { revealDelay, blocksFollowing });
  const onRevealRef = useRef(onReveal);

  useLayoutEffect(() => {
    onRevealRef.current = onReveal;
  });

  useEffect(() => {
    if (isVisible) {
      onRevealRef.current();
    }
  }, [isVisible]);

  return children({ visible: isVisible });
}

BaseScheduledNode.propTypes = {
  unitId: PropTypes.string.isRequired,
  isAnimationDisabled: PropTypes.bool,
  children: PropTypes.func.isRequired,
  revealDelay: PropTypes.number,
  blocksFollowing: PropTypes.bool,
  onReveal: PropTypes.func,
};
